import { GfProvider } from "@gf/ui";
import { SignupPage } from '../pages/auth/SignupPage';
import { useEffect } from "react";
import { createBrowserRouter, Navigate, Outlet, ScrollRestoration } from "react-router";
import { NotFound } from "../components/Forbidden";
import { FullPageLoader } from "../components/FullPage";
import { featureRoutes } from "../features/registry";
import { AuthProvider, PublicOnly, RequireAuth, RequireRole } from "../lib/auth";
import { initSsoBridge } from "../lib/sso";
import { ForgotPage } from "../pages/auth/ForgotPage";
import { LoginPage } from "../pages/auth/LoginPage";
import { ResetAlias, SetPasswordPage } from "../pages/auth/SetPasswordPage";
import { SsoPage } from "../pages/auth/SsoPage";
import { ConnectExtensionPage } from "../pages/ConnectExtensionPage";
import { HomePage } from "../pages/HomePage";
import { OpenRouterCallbackPage } from "../pages/settings/OpenRouterCallbackPage";
import { SettingsPage } from "../pages/settings/SettingsPage";
import { AppLayout } from "./Layout";
import { RouteError } from "./RouteError";
import { ProductAnalytics } from "./ProductAnalytics";

/** Providers that need the router (toasts/dialogs can render <Link>, auth can navigate). */
function AppRoot() {
  useEffect(() => {
    initSsoBridge();
  }, []);
  return (
    <GfProvider>
      <AuthProvider>
        <ProductAnalytics />
        <ScrollRestoration />
        <Outlet />
      </AuthProvider>
    </GfProvider>
  );
}

export function createAppRouter() {
  return createBrowserRouter([
    {
      element: <AppRoot />,
      errorElement: <RouteError />,
      // Shown while a lazy route (admin, reseller, lazy feature pages) loads on first paint.
      hydrateFallbackElement: <FullPageLoader />,
      children: [
        {
          element: <PublicOnly />,
          children: [
            { path: "/login", element: <LoginPage /> },
            { path: "/forgot", element: <ForgotPage /> },
          ],
        },
        { path: '/preview/trial-upgrade', lazy: async () => ({ Component: (await import('../pages/auth/TrialUpgrade')).TrialUpgradePreview }) },
        // Invite/reset and SSO links work whether or not someone is signed in.
        { path: "/set-password", element: <SetPasswordPage /> },
        { path: '/signup', element: <SignupPage /> },
        { path: "/reset", element: <ResetAlias /> },
        { path: "/sso", element: <SsoPage /> },
        {
          element: <RequireAuth />,
          children: [
            { path: '/trial-upgrade', lazy: async () => ({ Component: (await import('../pages/auth/TrialUpgrade')).TrialUpgrade }) },
            {
              element: <AppLayout />,
              errorElement: <RouteError inline />,
              children: [
                { path: "/", element: <HomePage /> },
                { path: '/affiliates', lazy: async () => ({ Component: (await import('../pages/affiliates/AffiliatesPage')).AffiliatesPage }) },
                { path: "/help/:articleId?", lazy: async () => ({ Component: (await import("../features/help/HelpPage")).HelpPage }) },
                { path: "/research", element: <Navigate replace to="/dream100" /> },
                { path: "/settings", element: <SettingsPage /> },
                { path: "/plans", lazy: async () => ({ Component: (await import("../pages/plans/PlansPage")).PlansPage }) },
                { path: "/course", lazy: async () => ({ Component: (await import("../pages/CoursePage")).CoursePage }) },
                { path: "/settings/ai/openrouter", element: <OpenRouterCallbackPage /> },
                { path: "/connect-extension", element: <ConnectExtensionPage /> },
                {
                  // Download / install / update guide (extension distribution).
                  path: "/extension",
                  lazy: async () => ({ Component: (await import("../pages/extension/ExtensionPage")).ExtensionPage }),
                },
                {
                  // Admins can also use their own reseller customer workspace.
                  element: <RequireRole roles={["reseller", "admin"]} />,
                  children: [
                    {
                      path: "/reseller",
                      // Code-split: most customers never open this page.
                      lazy: async () => ({ Component: (await import("../pages/reseller/ResellerPage")).ResellerPage }),
                    },
                  ],
                },
                {
                  element: <RequireRole roles={["admin"]} />,
                  children: [
                    {
                      path: "/admin",
                      lazy: async () => ({ Component: (await import("../pages/admin/AdminPage")).AdminPage }),
                    },
                    { path: '/admin/free-signups', lazy: async () => ({ Component: (await import('../pages/admin/FreeAccountsPage')).FreeAccountsPage }) },
                    { path: '/admin/affiliates', lazy: async () => ({ Component: (await import('../pages/affiliates/AdminAffiliatesPage')).AdminAffiliatesPage }) },
                    {
                      path: "/admin/extension",
                      lazy: async () => ({ Component: (await import("../pages/extension/AdminReleasesPage")).AdminReleasesPage }),
                    },
                  ],
                },
                ...featureRoutes(),
                { path: "*", element: <NotFound /> },
              ],
            },
          ],
        },
      ],
    },
  ]);
}
